/**
 * Friendly, translated error-message mapping for toasts.
 *
 * Goal: never surface raw, technical server/stack noise to end users, while
 * still preserving genuinely useful business messages (e.g. policy rejections,
 * balance validations) that the backend intends to be user-facing.
 *
 * Resolution order:
 *   1. Known system error CODE (UNAUTHORIZED, RATE_LIMITED, …) → translated copy.
 *   2. Known system message PATTERN (network/timeout/auth) → translated copy.
 *   3. A server message that looks presentable (short, no stack/jargon) → shown.
 *   4. Otherwise → the caller's context-specific translated fallback.
 *
 * `t` is passed in so all copy is localized; the fallback is a translation KEY.
 */

import type { TFunction } from 'i18next';

interface ErrorLike {
  message?: string;
  errorCode?: string;
}

// Known server error codes → translation keys.
const CODE_KEYS: Record<string, string> = {
  UNAUTHORIZED: 'errors.unauthorized',
  FORBIDDEN: 'errors.forbidden',
  INSUFFICIENT_ROLE: 'errors.forbidden',
  RATE_LIMITED: 'errors.rateLimited',
  NONCE_NOT_FOUND: 'errors.nonceOutOfSync',
  PAYLOAD_SUPERSEDED: 'errors.superseded',
  VALIDATION_ERROR: 'errors.validation',
  NOT_FOUND: 'errors.notFound',
  INTERNAL_ERROR: 'errors.generic',
  // Security hardening (Oct 2026): WK login / critical-action / governance refusals.
  EMAIL_NOT_VERIFIED: 'errors.emailNotVerified',
  INVALID_CHALLENGE_TOKEN: 'errors.invalidChallengeToken',
  INVALID_CHALLENGE_FORMAT: 'errors.invalidChallengeFormat',
  SAME_KEY_TWICE: 'errors.sameKeyTwice',
  PUBKEY_MISMATCH: 'errors.pubkeyMismatch',
  GOVERNANCE_REQUIRED: 'errors.governanceRequired',
  // Proposal creation / retry / signing / vault setup refusals (EVM call
  // safety, token metadata, fee caps, retry safety, policy re-check).
  SELF_CALL: 'errors.selfCall',
  UPGRADE_CALL: 'errors.upgradeCall',
  UNDECODABLE_CALL: 'errors.undecodableCall',
  INVALID_CALL_VALUE: 'errors.invalidCallValue',
  INVALID_CUSTOM_DATA: 'errors.invalidCustomData',
  TOKEN_DECIMALS_MISMATCH: 'errors.tokenDecimalsMismatch',
  TOKEN_SYMBOL_MISMATCH: 'errors.tokenSymbolMismatch',
  INVALID_TOKEN_CONTRACT: 'errors.invalidTokenContract',
  FEE_TOO_HIGH: 'errors.feeTooHigh',
  ALREADY_REPAID: 'errors.alreadyRepaid',
  PROPOSAL_INVALIDATED: 'errors.proposalInvalidated',
  POLICY_RECHECK_FAILED: 'errors.policyRecheckFailed',
  VAULT_FROZEN: 'errors.vaultFrozen',
  VAULT_NOT_ACTIVE: 'errors.vaultNotActive',
  UNSUPPORTED_SIGNATURE_FORMAT: 'errors.unsupportedSignatureFormat',
  SAME_KEY_PUBKEYS: 'errors.sameKeyPubkeys',
  SAME_XPUB: 'errors.sameXpub',
  POLICY_LESS_STRICT: 'errors.policyLessStrict',
  // Vault descriptor pinning (VD). App side: the descriptor couldn't be used,
  // so nothing was sent to the wallet. Device side: SSP Wallet / SSP Key
  // refused the request after checking the descriptor against its pin.
  VAULT_DESCRIPTOR_UNAVAILABLE: 'errors.vaultDescriptorUnavailable',
  VAULT_DESCRIPTOR_NOT_READY: 'errors.vaultDescriptorNotReady',
  VAULT_DESCRIPTOR_BAD_RESPONSE: 'errors.vaultDescriptorBadResponse',
  VAULT_DESCRIPTOR_REQUEST_MISMATCH: 'errors.vaultDescriptorRequestMismatch',
  VAULT_DESCRIPTOR_CHANGED: 'errors.vaultConfigurationChanged',
  VAULT_DESCRIPTOR_MISSING: 'errors.vaultDescriptorMissing',
  VAULT_PIN_REFUSED: 'errors.vaultPinRefused',
  VAULT_DESCRIPTOR_INVALID: 'errors.vaultDescriptorRejected',
};

// Codes whose server message carries the specific detail the user needs (the
// fee cap, the field that is looser than the org policy, the policy rule that
// failed, the proposal that already pays). Shown when presentable; otherwise
// the mapped copy above.
const SERVER_DETAIL_CODES = new Set([
  'FEE_TOO_HIGH',
  'POLICY_LESS_STRICT',
  'POLICY_RECHECK_FAILED',
  'ALREADY_REPAID',
]);

// System-noise message patterns → translation keys.
const PATTERN_KEYS: Array<{ test: RegExp; key: string }> = [
  {
    test: /network error|failed to fetch|err_network|timeout|econn|etimedout/i,
    key: 'errors.network',
  },
  { test: /rate limit|too many requests|\b429\b/i, key: 'errors.rateLimited' },
  {
    test: /unauthorized|\b401\b|session (has )?expired|invalid session/i,
    key: 'errors.unauthorized',
  },
  {
    test: /forbidden|\b403\b|not permitted|permission denied/i,
    key: 'errors.forbidden',
  },
  { test: /\b5\d\d\b|internal server error/i, key: 'errors.generic' },
];

// Heuristic: does a raw message look like technical noise we should NOT show?
// The CONSTANT_CASE check requires an underscore — a plain uppercase word is
// normal user content (promo codes like SUMMER20, ticker symbols, org names)
// and must pass through.
function looksTechnical(msg: string): boolean {
  if (!msg || msg.length > 200) return true;
  return /(\bat\s+\w+|TypeError|ReferenceError|SyntaxError|Cannot read|is not (defined|a function)|undefined|ECONN|ETIMEDOUT|<[^>]+>|[{}]|stack|\b[A-Z][A-Z0-9]*_[A-Z0-9_]+\b)/.test(
    msg
  );
}

/**
 * Map an error to friendly, translated copy for a toast.
 *
 * @param err         the thrown value (Error, error-like object, or string)
 * @param t           i18next translate function
 * @param fallbackKey translation key for the context-specific fallback
 */
export function friendlyError(err: unknown, t: TFunction, fallbackKey = 'errors.generic'): string {
  // The helper works with dynamic keys (fallbackKey + the mapped keys below),
  // so cast past i18next's strict literal-key typing to a plain string signature.
  const tr = t as unknown as (key: string) => string;
  let message = '';
  let code: string | undefined;

  if (typeof err === 'string') {
    message = err;
  } else if (err instanceof Error) {
    message = err.message;
    code = (err as Error & { errorCode?: string }).errorCode;
  } else if (err && typeof err === 'object') {
    const e = err as ErrorLike;
    message = e.message ?? '';
    code = e.errorCode;
  }

  const codeKey = code ? CODE_KEYS[code] : undefined;
  if (codeKey) {
    if (code && SERVER_DETAIL_CODES.has(code) && message && !looksTechnical(message)) {
      return message;
    }
    return tr(codeKey);
  }
  for (const { test, key } of PATTERN_KEYS) {
    if (test.test(message)) return tr(key);
  }
  // A clean, human-readable server message is worth showing (policy rejections,
  // balance validation, etc.); technical noise falls through to the fallback.
  if (message && !looksTechnical(message)) return message;
  return tr(fallbackKey);
}

export default friendlyError;
